/**
 * Performs a deep merge of objects and returns a new merged object.
 *
 * This function recursively merges the properties of the source object into the target object.
 * If a property exists in both objects and both values are objects, they are merged recursively.
 * Otherwise, the value from the source object replaces the value in the target object.
 *
 * @param target - The target object to merge into.
 * @param source - The source object whose properties will be merged into the target.
 * @returns A new object with properties from both target and source, with source properties taking precedence.
 *
 * @remarks
 * - If source is falsy, returns the target unchanged.
 * - If target is falsy, returns the source.
 * - If source is not an object (or is null), returns the source.
 * - If source is an array, returns the source array without merging.
 * - Only plain objects are merged recursively, not arrays or other object types.
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  if (!source) return target;
  if (!target) return source as T;

  if (typeof source !== 'object' || source === null) {
    return source as T;
  }

  if (Array.isArray(source)) {
    return source as unknown as T;
  }

  const output = { ...target };

  Object.keys(source).forEach((key) => {
    const targetValue = (output as Record<string, unknown>)[`${key}`];
    const sourceValue = (source as Record<string, unknown>)[`${key}`];

    if (
      typeof sourceValue === 'object' &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object'
    ) {
      (output as Record<string, unknown>)[`${key}`] = deepMerge(targetValue, sourceValue);
    } else {
      (output as Record<string, unknown>)[`${key}`] = sourceValue;
    }
  });

  return output;
}
